import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles, videos } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId) || articles[0];
  const relatedArticles = articles.filter((a) => article.relatedIds.includes(a.id));

  const articleSEO = {
    title: `${article.title} | 蘑菇视频官网`,
    description: article.excerpt,
    keywords: article.tags.join(',') + ',蘑菇视频官网,蘑菇视频',
    canonicalUrl: `/article/${articleId}`,
    ogType: 'article',
    schemaData: {
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      image: article.image,
    },
  };

  return (
    <>
      <SEOHead {...articleSEO} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {article.views}阅读
                </span>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed [&_h2]:text-xl [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_p]:mb-4 [&_p]:leading-7"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200 mt-10 pt-8">
              <h2 className="text-xl font-heading text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group flex gap-4 bg-background-100 rounded-xl p-3 hover:bg-background-200/70 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-28 flex-shrink-0 aspect-video rounded-lg overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 py-1">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}